import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import {
  SaleReportRecord,
  SalesAnalysisRecord,
  ProductSalesRecord,
  ShopSalesRecord,
  SaleReportSearchParams,
  SaleReportResponse,
  StatisticsOverview
} from './sale-report.config';

@Injectable({
  providedIn: 'root',
})
export class SaleReportService {
  constructor(private http: HttpClient) {}

  // Sales Statistics
  getSalesStatistics(params: SaleReportSearchParams): Observable<SaleReportResponse<SaleReportRecord>> {
    const mockData: SaleReportRecord[] = [
      {
        id: '1',
        orderId: 'ORD-2024-001',
        orderDate: '2024-01-01',
        orderStatus: 'Delivered',
        paymentMethod: 'Credit Card',
        channel: 'Online Store',
        grossSales: 3245.00,
        discount: 150.00,
        refund: 0.00,
        netSales: 3095.00,
        tax: 247.60,
        shippingFee: 15.00,
        totalCollected: 3357.60,
        numberOfOrders: 45,
        averageOrderValue: 72.11,
      },
      {
        id: '2',
        orderId: 'ORD-2024-002',
        orderDate: '2024-01-02',
        orderStatus: 'Processing',
        paymentMethod: 'eWallet',
        channel: 'Mobile App',
        grossSales: 2876.00,
        discount: 200.00,
        refund: 0.00,
        netSales: 2676.00,
        tax: 214.08,
        shippingFee: 0.00,
        totalCollected: 2890.08,
        numberOfOrders: 38,
        averageOrderValue: 75.68,
      },
      {
        id: '3',
        orderId: 'ORD-2024-003',
        orderDate: '2024-01-03',
        orderStatus: 'Shipped',
        paymentMethod: 'COD',
        channel: 'Physical Store',
        grossSales: 3890.00,
        discount: 300.00,
        refund: 50.00,
        netSales: 3540.00,
        tax: 283.20,
        shippingFee: 25.00,
        totalCollected: 3848.20,
        numberOfOrders: 52,
        averageOrderValue: 74.81,
      },
    ];

    return of({
      content: mockData,
      totalElements: mockData.length,
      totalPages: 1,
      size: params.size || 20,
      number: params.page || 0,
    }).pipe(delay(300));
  }

  getStatisticsOverview(): Observable<StatisticsOverview> {
    return of({
      totalOrders: 1234,
      totalSales: 89456,
      totalItems: 567,
      averageOrder: 72.49,
      conversionRate: 15.2,
      totalCustomers: 987,
    }).pipe(delay(200));
  }

  // Sales Analysis
  getSalesAnalysis(params: SaleReportSearchParams): Observable<SaleReportResponse<SalesAnalysisRecord>> {
    const mockData: SalesAnalysisRecord[] = [
      {
        id: '1',
        datePeriod: '2024-01-01',
        channel: 'Online Store',
        regionArea: 'North America',
        totalOrders: 156,
        grossSales: 15600.00,
        netSales: 14820.00,
        refund: 780.00,
        averageOrderValue: 95.00,
        conversionRate: 15.2,
        salesGrowthRate: 12.3,
      },
      {
        id: '2',
        datePeriod: '2024-01-01',
        channel: 'Mobile App',
        regionArea: 'Europe',
        totalOrders: 89,
        grossSales: 8900.00,
        netSales: 8455.00,
        refund: 445.00,
        averageOrderValue: 95.00,
        conversionRate: 8.7,
        salesGrowthRate: 5.2,
      },
      {
        id: '3',
        datePeriod: '2024-01-01',
        channel: 'Physical Store',
        regionArea: 'Asia Pacific',
        totalOrders: 234,
        grossSales: 23400.00,
        netSales: 22230.00,
        refund: 1170.00,
        averageOrderValue: 95.00,
        conversionRate: 18.5,
        salesGrowthRate: 8.7,
      },
    ];

    return of({
      content: mockData,
      totalElements: mockData.length,
      totalPages: 1,
      size: params.size || 20,
      number: params.page || 0,
    }).pipe(delay(300));
  }

  // Product Sales
  getProductSales(params: SaleReportSearchParams): Observable<SaleReportResponse<ProductSalesRecord>> {
    const mockData: ProductSalesRecord[] = [
      {
        id: '1',
        productId: 'IP15-001',
        productName: 'iPhone 15 Pro',
        category: 'Electronics',
        brand: 'Apple',
        quantitySold: 156,
        returnedQuantity: 3,
        unitPrice: 999.00,
        grossSales: 155844.00,
        discountApplied: 15600.00,
        netSales: 140244.00,
        topSellingFlag: true,
      },
      {
        id: '2',
        productId: 'MB-001',
        productName: 'MacBook Pro M3',
        category: 'Electronics',
        brand: 'Apple',
        quantitySold: 89,
        returnedQuantity: 1,
        unitPrice: 1999.00,
        grossSales: 177911.00,
        discountApplied: 17800.00,
        netSales: 160111.00,
        topSellingFlag: true,
      },
      {
        id: '3',
        productId: 'AP-001',
        productName: 'AirPods Pro',
        category: 'Electronics',
        brand: 'Apple',
        quantitySold: 234,
        returnedQuantity: 5,
        unitPrice: 249.00,
        grossSales: 58266.00,
        discountApplied: 5800.00,
        netSales: 52466.00,
        topSellingFlag: false,
      },
    ];

    return of({
      content: mockData,
      totalElements: mockData.length,
      totalPages: 1,
      size: params.size || 20,
      number: params.page || 0,
    }).pipe(delay(300));
  }

  // Shop Sales
  getShopSales(params: SaleReportSearchParams): Observable<SaleReportResponse<ShopSalesRecord>> {
    const mockData: ShopSalesRecord[] = [
      {
        id: '1',
        shopStoreName: 'Tmall Flagship Store',
        channel: 'Marketplace',
        region: 'Asia Pacific',
        numberOfOrders: 456,
        grossSales: 45600.00,
        netSales: 43320.00,
        refund: 2280.00,
        shippingFee: 2280.00,
        topSellingProducts: 'iPhone 15, MacBook Pro, AirPods',
        salesGrowthRate: 20.1,
      },
      {
        id: '2',
        shopStoreName: 'JD Self-operated Store',
        channel: 'Marketplace',
        region: 'Asia Pacific',
        numberOfOrders: 389,
        grossSales: 38900.00,
        netSales: 36955.00,
        refund: 1945.00,
        shippingFee: 1945.00,
        topSellingProducts: 'Samsung Galaxy, MacBook Air, iPad',
        salesGrowthRate: 17.2,
      },
      {
        id: '3',
        shopStoreName: 'Physical Store Beijing',
        channel: 'Physical Store',
        region: 'Asia Pacific',
        numberOfOrders: 234,
        grossSales: 23400.00,
        netSales: 22230.00,
        refund: 1170.00,
        shippingFee: 0.00,
        topSellingProducts: 'iPhone 15, MacBook Pro, Apple Watch',
        salesGrowthRate: 10.3,
      },
    ];

    return of({
      content: mockData,
      totalElements: mockData.length,
      totalPages: 1,
      size: params.size || 20,
      number: params.page || 0,
    }).pipe(delay(300));
  }

  // Export functionality
  exportSaleReport(params: SaleReportSearchParams): Observable<Blob> {
    // Mock export - in real app, this would return actual CSV/Excel blob
    let csvContent = '';
    
    // Generate different CSV content based on report type
    if (params.channel) {
      // Sales Statistics export
      csvContent = 'Order ID,Order Date,Order Status,Payment Method,Channel,Gross Sales,Discount,Refund,Net Sales,Tax,Shipping Fee,Total Collected,Number of Orders,Average Order Value\n';
      csvContent += 'ORD-2024-001,2024-01-01,Delivered,Credit Card,Online Store,3245.00,150.00,0.00,3095.00,247.60,15.00,3357.60,45,72.11\n';
    } else if (params.regionArea) {
      // Sales Analysis export
      csvContent = 'Date/Period,Channel,Region/Area,Total Orders,Gross Sales,Net Sales,Refund,Average Order Value,Conversion Rate,Sales Growth Rate\n';
      csvContent += '2024-01-01,Online Store,North America,156,15600.00,14820.00,780.00,95.00,15.2,12.3\n';
    } else if (params.productId) {
      // Product Sales export
      csvContent = 'Product ID/SKU,Product Name,Category,Brand,Quantity Sold,Returned Quantity,Unit Price,Gross Sales,Discount Applied,Net Sales,Top-selling Flag\n';
      csvContent += 'IP15-001,iPhone 15 Pro,Electronics,Apple,156,3,999.00,155844.00,15600.00,140244.00,Yes\n';
    } else {
      // Shop Sales export
      csvContent = 'Shop/Store Name,Channel,Region,Number of Orders,Gross Sales,Net Sales,Refund,Shipping Fee,Top-selling Products,Sales Growth Rate\n';
      csvContent += 'Tmall Flagship Store,Marketplace,Asia Pacific,456,45600.00,43320.00,2280.00,2280.00,iPhone 15 MacBook Pro AirPods,20.1\n';
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    return of(blob).pipe(delay(500));
  }
}
